import React, { useState } from "react"
import Link from "next/link"
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, SlidersHorizontal, ArrowUpRight, ArrowDownRight, Check, AlertTriangle, Info, X } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

/**
 * Design Tokens — ADEN CRM
 * Fuente única de verdad para clases de estilo.
 * - Tipografía: text-sm (14px) para body, text-xs para labels/hints
 * - Inputs: h-9 estándar, rounded-lg
 * - Cards: rounded-xl
 * - Colores de estado: usar CSS variables semánticas (no hardcode emerald/rose/amber)
 */
export const crmStyles = {
  // ── Layout & Containers ───────────────────────────────────────
  sectionCard: "bg-card border border-border rounded-xl p-5 space-y-4",
  sectionHeader: "flex flex-col gap-1 mb-2",
  sectionTitle: "text-sm font-semibold tracking-tight text-foreground",
  sectionDescription: "text-xs text-muted-foreground leading-relaxed",

  // ── Form Elements (h-9 estándar, rounded-lg) ─────────────────
  input: "w-full h-9 rounded-lg border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-shadow",
  textarea: "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-shadow",
  select: "w-full h-9 rounded-lg border border-border bg-background px-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-shadow cursor-pointer",

  // ── Typography ────────────────────────────────────────────────
  label: "block text-xs font-medium text-foreground/80 mb-1.5",
  hint: "block text-xs text-muted-foreground mt-1.5",

  // ── Tables ────────────────────────────────────────────────────
  tableTh: "px-3 py-2.5 text-left text-[11px] font-medium text-muted-foreground whitespace-nowrap normal-case tracking-normal bg-muted border-b border-border select-none transition-colors",
  tableTd: "px-4 py-3 text-sm text-foreground border-b border-border",
  tableRow: "hover:bg-muted transition-colors cursor-pointer",

  // ── Buttons & Icon Buttons ────────────────────────────────────
  buttonIcon: "p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent/80 rounded-md transition-all",
  buttonIconDestructive: "p-1.5 text-muted-foreground hover:text-status-error hover:bg-status-error-bg rounded-md transition-all",

  // ── Status Badge (usar con getEstadoTurno / NIVEL_STOCK) ──────
  // Ejemplo: <span className={cn(crmStyles.badge, estado.text, estado.bg, estado.border)}>
  badge: "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border",

  // ── Mobile Layout Tokens ───────────────────────────────────────
  // Header móvil: h-14 (56px), sticky top-0 z-30
  // BottomNav:    h-14 (56px), fixed bottom-0
  // Contenido:    pb-16 md:pb-8 (compensar BottomNav en móvil)
  // Touch target: min-h-[44px] mínimo para elementos interactivos
  // Breakpoints:  base = móvil (<768px) | md: = tablet/desktop
  mobileCard: "bg-card border border-border rounded-xl p-4 space-y-2",
  mobileListItem: "flex items-center justify-between gap-3 py-3 border-b border-border last:border-0",
  mobileSectionTitle: "text-xs font-bold tracking-widest uppercase text-muted-foreground px-1 mb-2",
}

// ── CRMHeader ─────────────────────────────────────────────────────────────────

export function CRMHeader({ title, subtitle, children }: {
  title: string
  subtitle?: string
  children?: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-y-3 gap-x-4 mb-6 md:mb-8 animate-fade-in">
      <div className="min-w-0 flex-1">
        <h2 className="text-xl md:text-2xl font-bold tracking-tight text-foreground leading-tight">{title}</h2>
        {subtitle && <p className="text-xs md:text-sm text-muted-foreground mt-1 leading-snug">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 shrink-0">{children}</div>}
    </div>
  )
}

// ── CRMSection ────────────────────────────────────────────────────────────────

export function CRMSection({ title, description, icon, children, className = "", action }: {
  title: string
  description?: string
  icon?: React.ReactNode
  children: React.ReactNode
  className?: string
  action?: React.ReactNode
}) {
  return (
    <div className={cn(crmStyles.sectionCard, className)}>
      <div className={crmStyles.sectionHeader}>
        <div className="flex items-center gap-2">
          {icon && <div>{icon}</div>}
          <h3 className={crmStyles.sectionTitle}>{title}</h3>
        </div>
        {description && <p className={crmStyles.sectionDescription}>{description}</p>}
      </div>
      <div className="pt-2">{children}</div>
      {action && <div className="mt-3">{action}</div>}
    </div>
  )
}

// ── CRMSortableHeader ─────────────────────────────────────────────────────────

export function CRMSortableHeader<T extends string>({
  label,
  sortKey,
  currentSortKey,
  currentSortDir,
  onSort,
  className = "",
}: {
  label: string
  sortKey: T
  currentSortKey: string
  currentSortDir: 'asc' | 'desc'
  onSort: (key: T) => void
  className?: string
}) {
  const isActive = sortKey === currentSortKey

  return (
    <th
      className={cn(crmStyles.tableTh, "cursor-pointer hover:bg-muted/50 group", className)}
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1.5">
        {label}
        <span className={cn(
          "transition-all duration-200",
          isActive ? "text-primary opacity-100" : "text-muted-foreground/30 opacity-0 group-hover:opacity-100"
        )}>
          {!isActive && <ChevronsUpDown className="size-3" />}
          {isActive && currentSortDir === 'asc'  && <ChevronUp   className="size-3" />}
          {isActive && currentSortDir === 'desc' && <ChevronDown  className="size-3" />}
        </span>
      </div>
    </th>
  )
}

// ── CRMModal ──────────────────────────────────────────────────────────────────

export function CRMModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  maxWidth = 'sm',
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  children: React.ReactNode
  footer?: React.ReactNode
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
}) {
  const widthClasses = {
    sm:  'sm:max-w-sm',
    md:  'sm:max-w-md',
    lg:  'sm:max-w-lg',
    xl:  'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(widthClasses[maxWidth], "animate-in slide-in-from-bottom-2")}>
        <DialogHeader className="mb-2">
          <DialogTitle className="text-lg font-bold tracking-tight">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="py-2">{children}</div>
        {footer && <DialogFooter className="mt-4">{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  )
}

// ── CRMStatusBadge ────────────────────────────────────────────────────────────
// Componente reutilizable para cualquier badge de estado

export function CRMStatusBadge({
  text,
  textClass,
  bgClass,
  borderClass,
  dot = false,
  dotClass,
}: {
  text: string
  textClass: string
  bgClass: string
  borderClass: string
  dot?: boolean
  dotClass?: string
}) {
  return (
    <span className={cn(crmStyles.badge, textClass, bgClass, borderClass)}>
      {dot && dotClass && <span className={cn("size-1.5 rounded-full", dotClass)} />}
      {text}
    </span>
  )
}

// ── CRMEmptyState ─────────────────────────────────────────────────────────────

export function CRMEmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: React.ReactNode
  title: string
  description?: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
      {icon && <div className="text-muted-foreground/40 mb-1">{icon}</div>}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description && <p className="text-xs text-muted-foreground max-w-xs">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

// ── CRMLoadingRow ─────────────────────────────────────────────────────────────

export function CRMLoadingRows({ cols, rows = 5 }: { cols: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="border-b border-border/40">
          {Array.from({ length: cols }).map((_, j) => (
            <td key={j} className={crmStyles.tableTd}>
              <div className="h-4 bg-muted/60 rounded animate-pulse" />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

// ── PageKPIs ──────────────────────────────────────────────────────────────────

interface KPIItem {
  label: string
  value: string | number
  color?: string
  isLoading?: boolean
}

const gridColsMap: Record<2 | 3 | 4, string> = {
  2: 'grid-cols-2 sm:grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4',
}

export function PageKPIs({ items, cols = 4 }: { items: KPIItem[]; cols?: 2 | 3 | 4 }) {
  return (
    <div className={`grid ${gridColsMap[cols]} gap-3`}>
      {items.map(k => (
        <div key={k.label} className="rounded-xl border border-border bg-card px-3 py-3">
          {k.isLoading ? (
            <div className="h-7 w-24 bg-muted/60 rounded animate-pulse" />
          ) : (
            <>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 truncate">{k.label}</p>
              <p className={cn("text-base font-bold truncate", k.color)}>{k.value}</p>
            </>
          )}
        </div>
      ))}
    </div>
  )
}

// ── PageFilters ───────────────────────────────────────────────────────────────

export const filterClass = 'h-9 px-2.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-ring'

interface PageFiltersProps {
  searchValue: string
  onSearchChange: (v: string) => void
  searchPlaceholder?: string
  children?: React.ReactNode
  extraFilters?: React.ReactNode
  hasActiveFilters?: boolean
  onClearFilters?: () => void
}

export function PageFilters({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Buscar…",
  children,
  extraFilters,
  hasActiveFilters,
  onClearFilters,
}: PageFiltersProps) {
  const [showMore, setShowMore] = useState(false)

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <input
            value={searchValue}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className={cn(filterClass, "pl-8 w-full")}
          />
        </div>
        {children}
        {extraFilters && (
          <button
            type="button"
            onClick={() => setShowMore(v => !v)}
            className={cn(
              "flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm transition-colors",
              showMore
                ? "border-primary text-primary bg-primary/5"
                : "border-border text-muted-foreground hover:text-foreground"
            )}
          >
            <SlidersHorizontal className="size-3.5" />
            <span className="hidden sm:inline">Filtros</span>
            {hasActiveFilters && !showMore && (
              <span className="size-1.5 rounded-full bg-primary" />
            )}
          </button>
        )}
        {hasActiveFilters && onClearFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="h-9 px-3 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Limpiar
          </button>
        )}
      </div>
      {showMore && extraFilters && (
        <div className="flex flex-wrap gap-2 items-center p-3 rounded-lg border border-border bg-muted">
          {extraFilters}
        </div>
      )}
    </div>
  )
}

// ── ModalFormShell ────────────────────────────────────────────────────────────
// Shell reutilizable (sin Dialog). Usar en formularios standalone que viven
// dentro de un Dialog externo (paciente-form, turno-form, orden-laboratorio-form).

interface ModalFormShellProps {
  title: string
  subtitle?: string
  icon?: React.ReactNode
  onClose: () => void
  children: React.ReactNode
  footer: React.ReactNode
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
  /** @deprecated Ya no tiene efecto — el scroll se controla con flexbox */
  maxHeightPx?: number
  /** Pasar el nodo del header personalizado cuando el formulario requiere su
   *  propio header (ej: turno-form con búsqueda de paciente). El shell usa
   *  este header en lugar del estándar. */
  customHeader?: React.ReactNode
}

export function ModalFormShell({
  title,
  subtitle,
  icon,
  onClose,
  children,
  footer,
  onSubmit,
  maxHeightPx = 700,
  customHeader,
}: ModalFormShellProps) {
  const header = customHeader ?? (
    <div className="px-5 py-3.5 border-b border-border shrink-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <p className="text-sm font-semibold">{title}</p>
            {subtitle && (
              <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="flex items-center justify-center size-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  )

  const footerBar = (
    <div className="px-4 py-3 border-t border-border bg-muted flex items-center justify-between gap-2 shrink-0">
      {footer}
    </div>
  )

  const bodyContent = (
    <div className="overflow-y-auto px-5 py-4 space-y-4 flex-1 min-h-0">
      {children}
    </div>
  )

  return (
    <>
      {header}
      {onSubmit ? (
        <form onSubmit={onSubmit} className="flex flex-col overflow-hidden min-h-0 flex-1">
          {bodyContent}
          {footerBar}
        </form>
      ) : (
        <div className="flex flex-col overflow-hidden min-h-0 flex-1">
          {bodyContent}
          {footerBar}
        </div>
      )}
    </>
  )
}

// ── ModalForm ─────────────────────────────────────────────────────────────────
// Formulario modal completo (incluye Dialog). Para formularios que manejan
// su propio Dialog (la mayoría).

interface ModalFormProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  icon?: React.ReactNode
  maxWidth?: string
  /** @deprecated Ya no tiene efecto — el scroll se controla con flexbox */
  maxHeightPx?: number
  children: React.ReactNode
  footer: React.ReactNode
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
}

export function ModalForm({
  open,
  onClose,
  title,
  subtitle,
  icon,
  maxWidth = "max-w-lg",
  maxHeightPx = 700,
  children,
  footer,
  onSubmit,
}: ModalFormProps) {
  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent
        className={cn("w-[95vw]", maxWidth, "max-h-[90dvh] p-0 gap-0 overflow-hidden rounded-xl flex flex-col")}
        showCloseButton={false}
      >
        <ModalFormShell
          title={title}
          subtitle={subtitle}
          icon={icon}
          onClose={onClose}
          footer={footer}
          onSubmit={onSubmit}
          maxHeightPx={maxHeightPx}
        >
          {children}
        </ModalFormShell>
      </DialogContent>
    </Dialog>
  )
}

// ── CRMTable ──────────────────────────────────────────────────────────────────
// Responsive table: standard HTML table on desktop, card list on mobile.

export interface CRMTableColumn<T> {
  key: string
  header: React.ReactNode
  render: (item: T) => React.ReactNode
  sortable?: boolean
  hideOnMobile?: boolean
  className?: string
}

export interface CRMTableProps<T> {
  columns: CRMTableColumn<T>[]
  data: T[]
  keyExtractor: (item: T) => string
  onRowClick?: (item: T) => void
  isLoading?: boolean
  emptyState?: {
    icon?: React.ReactNode
    title: string
    description?: string
    action?: React.ReactNode
  }
  sortKey?: string
  sortDir?: 'asc' | 'desc'
  onSort?: (key: string) => void
}

export function CRMTable<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  isLoading = false,
  emptyState,
  sortKey,
  sortDir = 'asc',
  onSort,
}: CRMTableProps<T>) {
  const visibleOnMobile = columns.filter(c => !c.hideOnMobile)

  return (
    <>
      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto rounded-xl border border-border">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={cn(
                    crmStyles.tableTh,
                    col.sortable && onSort ? "cursor-pointer hover:bg-muted/50 group" : "",
                    col.className,
                  )}
                  onClick={() => col.sortable && onSort?.(col.key)}
                >
                  <div className="flex items-center gap-1.5">
                    {col.header}
                    {col.sortable && onSort && (
                      <span className={cn(
                        "transition-all duration-200",
                        sortKey === col.key
                          ? "text-primary opacity-100"
                          : "text-muted-foreground/30 opacity-0 group-hover:opacity-100",
                      )}>
                        {sortKey !== col.key && <ChevronsUpDown className="size-3" />}
                        {sortKey === col.key && sortDir === 'asc' && <ChevronUp className="size-3" />}
                        {sortKey === col.key && sortDir === 'desc' && <ChevronDown className="size-3" />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && <CRMLoadingRows cols={columns.length} />}
            {!isLoading && data.length === 0 && emptyState && (
              <tr>
                <td colSpan={columns.length}>
                  <CRMEmptyState {...emptyState} />
                </td>
              </tr>
            )}
            {!isLoading && data.map(item => (
              <tr
                key={keyExtractor(item)}
                className={cn(crmStyles.tableRow, onRowClick && "cursor-pointer")}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map(col => (
                  <td key={col.key} className={cn(crmStyles.tableTd, col.className)}>
                    {col.render(item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="sm:hidden space-y-2">
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-4 space-y-2">
                {visibleOnMobile.map((_, j) => (
                  <div key={j} className="h-4 bg-muted/60 rounded animate-pulse" style={{ width: `${60 + (j * 11) % 30}%` }} />
                ))}
              </div>
            ))}
          </div>
        )}
        {!isLoading && data.length === 0 && emptyState && (
          <CRMEmptyState {...emptyState} />
        )}
        {!isLoading && data.map(item => (
          <div
            key={keyExtractor(item)}
            className={cn(
              "rounded-xl border border-border bg-card p-4 space-y-2",
              onRowClick && "cursor-pointer hover:bg-muted/40 transition-colors",
            )}
            onClick={() => onRowClick?.(item)}
          >
            {visibleOnMobile.map(col => (
              <div key={col.key} className="flex items-start justify-between gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
                  {col.header}
                </span>
                <span className="text-sm text-foreground text-right">
                  {col.render(item)}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  )
}

// ── CRMKPIGrid ────────────────────────────────────────────────────────────────
// KPI grid with loading states and trend indicators.

export interface KPICardItem {
  label: string
  value: string | number
  trend?: { value: number; label?: string }
  isLoading?: boolean
  icon?: React.ReactNode
}

const kpiGridCols: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
  6: 'grid-cols-2 sm:grid-cols-3',
}

function resolveKPIGrid(count: number): string {
  return kpiGridCols[count] ?? 'grid-cols-2 sm:grid-cols-4'
}

export function CRMKPIGrid({ items }: { items: KPICardItem[] }) {
  return (
    <div className={cn("grid gap-3", resolveKPIGrid(items.length))}>
      {items.map((item, idx) => (
        <div
          key={idx}
          className={cn(
            "rounded-xl border border-border bg-card px-4 py-3 flex flex-col gap-1.5",
            items.length % 2 === 1 && idx === items.length - 1
              ? "col-span-2 sm:col-span-1"
              : ""
          )}
        >
          {item.isLoading ? (
            <div className="space-y-2">
              <div className="h-3 w-20 bg-muted/60 rounded animate-pulse" />
              <div className="h-6 w-28 bg-muted/60 rounded animate-pulse" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground truncate">
                  {item.label}
                </p>
                {item.icon && (
                  <span className="text-muted-foreground/60 shrink-0">{item.icon}</span>
                )}
              </div>
              <p className="text-lg font-bold tabular text-foreground truncate">{item.value}</p>
              {item.trend !== undefined && (
                <div className={cn(
                  "flex items-center gap-0.5 text-[11px] font-medium",
                  item.trend.value > 0 ? "text-status-success" : item.trend.value < 0 ? "text-status-error" : "text-muted-foreground",
                )}>
                  {item.trend.value > 0 && <ArrowUpRight className="size-3 shrink-0" />}
                  {item.trend.value < 0 && <ArrowDownRight className="size-3 shrink-0" />}
                  <span>
                    {item.trend.value > 0 ? '+' : ''}{item.trend.value}%
                    {item.trend.label && ` ${item.trend.label}`}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  )
}

// ── CRMPageSkeleton ───────────────────────────────────────────────────────────

export function CRMPageSkeleton({
  kpiCount = 4,
  tableRows = 6,
  tableCols = 5,
  showFilters = true,
}: {
  kpiCount?: number
  tableRows?: number
  tableCols?: number
  showFilters?: boolean
}) {
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header skeleton */}
      <div className="flex items-center justify-between mb-8">
        <div className="space-y-2">
          <div className="h-7 w-48 bg-muted/60 rounded animate-pulse" />
          <div className="h-4 w-64 bg-muted/60 rounded animate-pulse" />
        </div>
        <div className="h-9 w-28 bg-muted/60 rounded-lg animate-pulse" />
      </div>

      {/* KPI skeleton */}
      <CRMKPIGrid
        items={Array.from({ length: kpiCount }, () => ({ label: '', value: '', isLoading: true }))}
      />

      {/* Filters skeleton */}
      {showFilters && (
        <div className="flex gap-2">
          <div className="h-9 flex-1 bg-muted/60 rounded-lg animate-pulse" />
          <div className="h-9 w-24 bg-muted/60 rounded-lg animate-pulse" />
          <div className="h-9 w-24 bg-muted/60 rounded-lg animate-pulse" />
        </div>
      )}

      {/* Table skeleton */}
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="bg-muted border-b border-border px-4 py-3 flex gap-8">
          {Array.from({ length: tableCols }).map((_, i) => (
            <div key={i} className="h-3 bg-muted-foreground/20 rounded animate-pulse" style={{ width: `${50 + (i * 17) % 40}px` }} />
          ))}
        </div>
        <div className="divide-y divide-border">
          {Array.from({ length: tableRows }).map((_, i) => (
            <div key={i} className="px-4 py-3 flex gap-8 items-center">
              {Array.from({ length: tableCols }).map((_, j) => (
                <div key={j} className="h-4 bg-muted/60 rounded animate-pulse" style={{ width: `${60 + (j * 19 + i * 7) % 80}px` }} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── CRMBreadcrumb ─────────────────────────────────────────────────────────────

export interface BreadcrumbItem {
  label: string
  href?: string
}

export function CRMBreadcrumb({ items }: { items: BreadcrumbItem[] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4">
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1
        return (
          <React.Fragment key={idx}>
            {idx > 0 && <span className="text-muted-foreground/40" aria-hidden>/</span>}
            {isLast || !item.href ? (
              <span className={cn(isLast && "font-semibold text-foreground")}>{item.label}</span>
            ) : (
              <Link
                href={item.href}
                className="hover:text-foreground transition-colors"
              >
                {item.label}
              </Link>
            )}
          </React.Fragment>
        )
      })}
    </nav>
  )
}

// ── CRMTimeline ───────────────────────────────────────────────────────────────

export interface TimelineItem {
  id: string
  date: string
  title: string
  description?: string
  icon?: React.ReactNode
  type?: 'default' | 'success' | 'warning' | 'error' | 'info'
}

const timelineDotClass: Record<NonNullable<TimelineItem['type']>, string> = {
  default: 'bg-status-neutral border-status-neutral-border',
  success: 'bg-status-success border-status-success-border',
  warning: 'bg-status-warning border-status-warning-border',
  error:   'bg-status-error border-status-error-border',
  info:    'bg-status-info border-status-info-border',
}

const timelineIconClass: Record<NonNullable<TimelineItem['type']>, string> = {
  default: 'text-status-neutral',
  success: 'text-status-success',
  warning: 'text-status-warning',
  error:   'text-status-error',
  info:    'text-status-info',
}

export function CRMTimeline({ items, className }: { items: TimelineItem[]; className?: string }) {
  return (
    <ol className={cn("relative", className)} aria-label="Timeline">
      {items.map((item, idx) => {
        const type = item.type ?? 'default'
        const isLast = idx === items.length - 1
        return (
          <li key={item.id} className="flex gap-4">
            {/* Dot + vertical line column */}
            <div className="flex flex-col items-center shrink-0">
              <div
                className={cn(
                  "size-3 rounded-full border-2 mt-1 shrink-0",
                  timelineDotClass[type],
                )}
                aria-hidden
              />
              {!isLast && <div className="w-px flex-1 bg-border mt-1" aria-hidden />}
            </div>

            {/* Content column */}
            <div className={cn("pb-6 min-w-0", isLast && "pb-0")}>
              <div className="flex items-center gap-2 mb-0.5">
                {item.icon && (
                  <span className={cn("shrink-0", timelineIconClass[type])} aria-hidden>
                    {item.icon}
                  </span>
                )}
                <p className="text-sm font-semibold text-foreground leading-tight truncate">{item.title}</p>
              </div>
              <time
                className="block text-[11px] text-muted-foreground mb-1"
                dateTime={item.date}
              >
                {item.date}
              </time>
              {item.description && (
                <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

// ── CRMWizard ─────────────────────────────────────────────────────────────────

export interface WizardStep {
  id: string
  label: string
  icon?: React.ReactNode
}

export function CRMWizard({
  steps,
  currentStep,
  onStepClick,
}: {
  steps: WizardStep[]
  currentStep: number
  onStepClick?: (step: number) => void
}) {
  return (
    <nav aria-label="Wizard steps" className="w-full">
      <ol className="flex items-center w-full">
        {steps.map((step, idx) => {
          const isCompleted = idx < currentStep
          const isCurrent = idx === currentStep
          const isClickable = onStepClick !== undefined && (isCompleted || isCurrent)
          const isLast = idx === steps.length - 1

          return (
            <li
              key={step.id}
              className={cn(
                "flex items-center",
                isLast ? "" : "flex-1",
              )}
            >
              {/* Step circle */}
              <button
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onStepClick?.(idx)}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={`Paso ${idx + 1}: ${step.label}${isCompleted ? ' (completado)' : ''}`}
                className={cn(
                  "flex flex-col items-center gap-1.5 group shrink-0",
                  isClickable ? "cursor-pointer" : "cursor-default",
                )}
              >
                <span
                  className={cn(
                    "flex items-center justify-center size-8 rounded-full border-2 text-xs font-bold transition-all duration-200",
                    isCompleted && "bg-primary border-primary text-primary-foreground",
                    isCurrent && "bg-background border-primary text-primary ring-2 ring-primary/20",
                    !isCompleted && !isCurrent && "bg-background border-border text-muted-foreground",
                  )}
                >
                  {isCompleted
                    ? <Check className="size-4" aria-hidden />
                    : step.icon ?? <span>{idx + 1}</span>
                  }
                </span>
                <span
                  className={cn(
                    "hidden sm:block text-[11px] font-medium text-center leading-tight max-w-[72px] transition-colors",
                    isCurrent && "text-primary font-semibold",
                    isCompleted && "text-foreground",
                    !isCompleted && !isCurrent && "text-muted-foreground",
                  )}
                >
                  {step.label}
                </span>
                {/* Mobile: only show number */}
                <span className="sm:hidden text-[10px] font-medium text-center text-muted-foreground">
                  {idx + 1}
                </span>
              </button>

              {/* Connector line */}
              {!isLast && (
                <div
                  className={cn(
                    "flex-1 h-0.5 mx-2 transition-colors duration-200",
                    isCompleted ? "bg-primary" : "bg-border",
                  )}
                  aria-hidden
                />
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

// ── MobileListCard ────────────────────────────────────────────────────────────
// Wrapper estándar para items de lista en móvil.
// Uso:
//   <MobileListCard
//     title="Julio Cardozo"
//     subtitle="DNI 95578194"
//     badge={<span>Activo</span>}
//     meta="$140.000"
//     onClick={() => router.push('/pacientes/95578194')}
//   />
export function MobileListCard({
  title,
  subtitle,
  badge,
  meta,
  onClick,
  className,
}: {
  title: string
  subtitle?: string
  badge?: React.ReactNode
  meta?: React.ReactNode
  onClick?: () => void
  className?: string
}) {
  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      className={cn(
        crmStyles.mobileCard,
        onClick && "cursor-pointer hover:bg-accent/50 active:scale-[0.99] transition-all",
        className
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground truncate">{title}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</p>}
        </div>
        {badge && <div className="shrink-0">{badge}</div>}
      </div>
      {meta && <div className="text-sm font-semibold text-foreground">{meta}</div>}
    </div>
  )
}

// ── CRMConfirmDialog ──────────────────────────────────────────────────────────

export function CRMConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  variant = 'danger',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  isLoading = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  onConfirm: () => void
  variant?: 'danger' | 'warning'
  confirmText?: string
  cancelText?: string
  isLoading?: boolean
}) {
  const isDanger = variant === 'danger'

  return (
    <Dialog open={open} onOpenChange={isLoading ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <span
              className={cn(
                "flex items-center justify-center size-9 rounded-full shrink-0",
                isDanger ? "bg-status-error-bg text-status-error" : "bg-status-warning-bg text-status-warning",
              )}
              aria-hidden
            >
              {isDanger
                ? <X className="size-4" />
                : <AlertTriangle className="size-4" />
              }
            </span>
            <DialogTitle className="text-base font-bold leading-tight">{title}</DialogTitle>
          </div>
          {description && (
            <DialogDescription className="text-sm text-muted-foreground leading-relaxed pl-12">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter className="mt-2 flex gap-2 sm:flex-row-reverse">
          <button
            type="button"
            disabled={isLoading}
            onClick={onConfirm}
            className={cn(
              "inline-flex items-center justify-center h-9 px-4 rounded-lg text-sm font-semibold transition-all",
              "disabled:opacity-60 disabled:cursor-not-allowed",
              isDanger
                ? "bg-status-error text-white hover:opacity-90"
                : "bg-status-warning text-white hover:opacity-90",
            )}
            aria-busy={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="size-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" aria-hidden />
                {confirmText}
              </span>
            ) : confirmText}
          </button>
          <button
            type="button"
            disabled={isLoading}
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center justify-center h-9 px-4 rounded-lg text-sm font-medium border border-border text-foreground hover:bg-muted transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
